import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "en" | "nl";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    "nav.services": "Services",
    "nav.pricing": "Packages",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.book": "Book a Call",
    
    "hero.badge": "Digital Growth & Automation",
    "hero.title1": "Websites and automation",
    "hero.title2": "built for growth",
    "hero.desc": "ALEXAI builds websites, funnels, and automated workflows that help your business look more professional, generate more leads, and operate more efficiently.",
    "hero.btn1": "Book a Strategy Call",
    "hero.btn2": "Explore Our Approach",
    
    "services.badge": "Our Solutions",
    "services.title1": "Digital systems",
    "services.title2": "that drive growth.",
    "services.desc": "From websites and funnels to automation and AI: ALEXAI builds systems that enhance your visibility, streamline processes, and help generate more leads.",
    "services.1.title": "Websites & Funnels",
    "services.1.desc": "Professional websites and sales funnels that turn visitors into customers.",
    "services.2.title": "Unified Communication",
    "services.2.desc": "Manage all your emails, SMS, and DMs seamlessly from one powerful inbox.",
    "services.3.title": "Smart Scheduling",
    "services.3.desc": "Automated calendar management and appointments without the back-and-forth.",
    "services.4.title": "Automated Workflows",
    "services.4.desc": "Save time by smartly automating repetitive tasks and processes.",
    "services.5.title": "AI Agents",
    "services.5.desc": "Leverage AI for customer interaction, lead follow-up, and streamlining your operations.",
    
    "contact.badge": "Get in Touch",
    "contact.title1": "Start a",
    "contact.title2": "Project",
    "contact.desc": "Tell us about your goals. Our engineering team will analyze your needs and propose an optimal AI architecture.",
    "contact.email": "Email",
    "contact.location": "Location",
    "contact.form.fname": "First Name",
    "contact.form.lname": "Last Name",
    "contact.form.email": "Email Address",
    "contact.form.details": "Project Details",
    "contact.form.placeholder.details": "How can we help you?",
    "contact.form.submit": "Send Message",
    "contact.form.submitting": "Sending...",
    "contact.form.next": "Next Step",
    "contact.form.prev": "Back",
    "contact.form.step1.title": "What do you need help with?",
    "contact.form.step1.opt1": "New Website",
    "contact.form.step1.opt2": "Automations & AI",
    "contact.form.step1.opt3": "Both / Full System",
    "contact.form.step2.title": "What is your budget?",
    "contact.form.step2.opt1": "Under €2,000",
    "contact.form.step2.opt2": "€2,000 - €5,000",
    "contact.form.step2.opt3": "Over €5,000",
    "contact.form.step3.title": "Your details",
    "contact.toast.title": "Transmission Successful",
    "contact.toast.desc": "Our systems have received your inquiry.",
    
    "pagespeed.title1": "See how your",
    "pagespeed.title2": "website performs",
    "pagespeed.desc": "Enter your URL below to get a free simulated performance audit. We can help you reach a perfect 100 on Google's Core Web Vitals.",
    "pagespeed.input": "https://yourwebsite.com",
    "pagespeed.button": "Start Audit",
    "pagespeed.scanning": "Analyzing...",
    "pagespeed.scanning.desc": "This may take 15-30 seconds.",
    "pagespeed.result.title": "Analysis Complete",
    "pagespeed.result.desc": "We've simulated an analysis of your website. Let's discuss how we can improve these metrics in reality.",
    "pagespeed.result.metric1": "Performance",
    "pagespeed.result.metric2": "Accessibility",
    "pagespeed.result.metric3": "SEO",
    "pagespeed.result.metric4": "Best Practices",
    "pagespeed.result.detailed": "Detailed Core Web Vitals",
    "pagespeed.result.cta": "Get Full Report",
    "pagespeed.result.scan_another": "Scan Another",
    "pagespeed.error.demand": "The analysis service is currently experiencing high demand. Please try again in a few minutes or contact us for a manual audit.",
    "pagespeed.error.unreachable": "We couldn't reach that website. Please check the URL and make sure the site is publicly accessible.",
    "pagespeed.error.generic": "An error occurred during analysis.",
    
    "footer.desc": "Intelligent solutions for the future.",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.rights": "All rights reserved.",
    
    "about.title1": "Pioneering the future of",
    "about.title2": "enterprise intelligence.",
    "about.desc": "Based in Lommel, ALEXAI was founded on a simple premise: Artificial Intelligence shouldn't be a buzzword - it should be a measurable driver of business growth. We build bespoke AI solutions, from intelligent CRMs to custom SaaS platforms, designed to elevate your operations.",
    "about.val1.title": "Intelligent Innovation",
    "about.val1.desc": "We leverage the latest advancements in AI to build solutions that keep you ahead of the curve.",
    "about.val2.title": "Enterprise Reliability",
    "about.val2.desc": "Security, scalability, and robust architecture are at the core of everything we engineer.",
    "about.val3.title": "Rapid Execution",
    "about.val3.desc": "We turn complex AI concepts into deployed, value-generating systems in record time.",
    
    "process.badge": "How We Work",
    "process.title1": "Our",
    "process.title2": "Process",
    "process.desc": "A proven, transparent methodology to build your digital growth engine.",
    "process.1.title": "Analysis & Strategy",
    "process.1.desc": "We audit your current setup and define a tailored digital roadmap.",
    "process.2.title": "Build & Implementation",
    "process.2.desc": "We design and engineer your custom website and integration architecture.",
    "process.3.title": "Automation & Integration",
    "process.3.desc": "We connect your CRM, AI chatbots, and booking systems for seamless operation.",
    "process.4.title": "Optimization & Support",
    "process.4.desc": "We monitor performance, optimize conversion rates, and provide ongoing support.",

    "deliverables.badge": "What You Get",
    "deliverables.title1": "Concrete",
    "deliverables.title2": "Deliverables",
    "deliverables.desc": "We don't just build websites. We deliver complete digital systems designed to generate leads and scale your business.",
    "deliverables.1": "Custom High-Converting Website",
    "deliverables.2": "Automated Lead Capture Flow",
    "deliverables.3": "Seamless CRM Integration",
    "deliverables.4": "Intelligent AI Chatbot",
    "deliverables.5": "Smart Booking System",
    "deliverables.6": "Custom Automation Workflows",
    "deliverables.7": "Automated Follow-up Systems",
    "deliverables.8": "Insightful Reporting",

    "trust.badge": "Why ALEXAI",
    "trust.title1": "A Partner You Can",
    "trust.title2": "Trust",
    "trust.desc": "We believe in transparency, measurable results, and tailored solutions.",
    "trust.1.title": "Transparent Way of Working",
    "trust.1.desc": "No hidden fees or confusing technical jargon. You always know exactly what we are doing and why.",
    "trust.2.title": "Focus on Measurable Growth",
    "trust.2.desc": "Everything we build is designed to improve visibility, credibility, and conversions.",
    "trust.3.title": "Modern Tools & Systems",
    "trust.3.desc": "We leverage the latest technologies to ensure your digital infrastructure is fast, secure, and future-proof.",
    "trust.4.title": "Tailored Approach",
    "trust.4.desc": "No cookie-cutter templates. We build systems specifically adapted to your unique business needs.",

    "contact.btn1": "Request a Free Digital Audit",
    
    "about.vision.title": "The Vision Behind ALEXAI",
    "about.vision.desc": "ALEXAI was founded with a singular focus: to bridge the gap between complex digital technology and tangible business growth. Our founder recognized that many businesses struggle not because they lack a great product, but because their digital infrastructure fails to support their ambition. We believe in building transparent, highly effective digital systems that work tirelessly in the background, so you can focus on what you do best.",
    
    "testimonials.badge": "Client Success",
    "testimonials.title1": "Proven",
    "testimonials.title2": "Results",
    "testimonials.desc": "See how we've helped businesses transform their digital presence and automate their growth.",
    "testimonials.placeholder": "Case studies and client success stories will be featured here soon.",
    
    "roi.badge": "ROI Calculator",
    "roi.title1": "Calculate Your",
    "roi.title2": "Savings",
    "roi.desc": "Find out how much time and money our automated systems can save your business.",
    "roi.hours": "Manual hours spent per week (e.g., data entry, emails)",
    "roi.rate": "Average hourly rate (€)",
    "roi.saved.time": "Hours saved per month",
    "roi.saved.money": "Money saved per year",
    "roi.cta": "Start Automating",

    "chatbot.title": "ALEXAI Assistant",
    "chatbot.status": "Online",
    "chatbot.placeholder": "Type your message...",
    "chatbot.welcome": "Hi there! I'm ALEXAI's digital assistant. How can I help you today?",
    "chatbot.response": "Thanks for your message. Since this is a demo, I can't answer directly right now, but you can book a call with our team to see how I can work for your business!",

    "cases.badge": "Portfolio",
    "cases.title1": "Recent",
    "cases.title2": "Case Studies",
    "cases.desc": "Explore some of our recent projects and see how we've transformed digital operations for our clients.",
    "cases.view": "View Project",

    "blog.badge": "Blog",
    "blog.title": "Insights on websites, automation and digital growth",
    "blog.desc": "Practical articles on websites, funnels, automation and how businesses can approach their digital operations smarter.",
    "blog.category.all": "All",
    "blog.view_all": "View all articles",
    "blog.cta.title": "Ready to approach your digital foundation smarter?",
    "blog.cta.desc": "ALEXAI builds websites, funnels and automated systems for businesses that want to grow more professionally online.",
    "blog.cta.btn": "Book a Strategy Call",
    "blog.more": "More articles"
  },
  nl: {
    "nav.services": "Diensten",
    "nav.pricing": "Pakketten",
    "nav.about": "Over ons",
    "nav.contact": "Contact",
    "nav.book": "Boek een gesprek",
    
    "hero.badge": "Digitale Groei & Automatisatie",
    "hero.title1": "Websites en automatisatie",
    "hero.title2": "gebouwd voor groei.",
    "hero.desc": "ALEXAI bouwt websites, funnels en geautomatiseerde workflows die uw bedrijf helpen om professioneler over te komen, meer leads te genereren en efficiënter te werken.",
    "hero.btn1": "Boek een strategiegesprek",
    "hero.btn2": "Ontdek onze aanpak",
    
    "services.badge": "Onze oplossingen",
    "services.title1": "Digitale systemen",
    "services.title2": "die groei ondersteunen.",
    "services.desc": "Van websites en funnels tot automatisatie en AI: ALEXAI bouwt systemen die uw zichtbaarheid versterken, processen stroomlijnen en meer aanvragen helpen genereren.",
    "services.1.title": "Websites & funnels",
    "services.1.desc": "Professionele websites en sales funnels die bezoekers omzetten in klanten.",
    "services.2.title": "Centrale communicatie",
    "services.2.desc": "Beheer al uw e-mails, sms-berichten en DM's overzichtelijk vanuit één krachtige inbox.",
    "services.3.title": "Slimme planning",
    "services.3.desc": "Automatisch agendabeheer en afspraken zonder onnodig heen-en-weer gemail.",
    "services.4.title": "Geautomatiseerde workflows",
    "services.4.desc": "Bespaar tijd door repetitieve taken en processen slim te automatiseren.",
    "services.5.title": "AI-agents",
    "services.5.desc": "Zet AI in voor klantinteractie, leadopvolging en het stroomlijnen van uw bedrijfsvoering.",
    
    "contact.badge": "Contact",
    "contact.title1": "Start uw",
    "contact.title2": "project",
    "contact.desc": "Op zoek naar een website en systeem dat meer doet dan alleen goed ogen? Vertel ons over uw doelen, en wij werken een plan op maat uit.",
    "contact.email": "E-mail",
    "contact.location": "Locatie",
    "contact.form.fname": "Voornaam",
    "contact.form.lname": "Achternaam",
    "contact.form.email": "E-mailadres",
    "contact.form.details": "Projectdetails",
    "contact.form.placeholder.details": "Hoe kunnen we u helpen?",
    "contact.form.submit": "Verstuur bericht",
    "contact.form.submitting": "Verzenden...",
    "contact.form.next": "Volgende Stap",
    "contact.form.prev": "Terug",
    "contact.form.step1.title": "Waarmee kunnen we u helpen?",
    "contact.form.step1.opt1": "Nieuwe Website",
    "contact.form.step1.opt2": "Automatisatie & AI",
    "contact.form.step1.opt3": "Beide / Compleet Systeem",
    "contact.form.step2.title": "Wat is uw budget?",
    "contact.form.step2.opt1": "Onder €2.000",
    "contact.form.step2.opt2": "€2.000 - €5.000",
    "contact.form.step2.opt3": "Boven €5.000",
    "contact.form.step3.title": "Uw gegevens",
    "contact.toast.title": "Bericht verzonden",
    "contact.toast.desc": "Wij hebben uw aanvraag goed ontvangen en nemen snel contact op.",
    
    "pagespeed.title1": "Ontdek hoe uw",
    "pagespeed.title2": "website presteert",
    "pagespeed.desc": "Voer hieronder uw URL in en ontdek hoe uw website presteert. Wij helpen u richting sterke scores op Google Core Web Vitals.",
    "pagespeed.input": "https://uwwebsite.be",
    "pagespeed.button": "Start analyse",
    "pagespeed.scanning": "Analyseren...",
    "pagespeed.scanning.desc": "Dit kan 15-30 seconden duren.",
    "pagespeed.result.title": "Analyse voltooid",
    "pagespeed.result.desc": "We hebben een analyse van uw website gesimuleerd. Laten we bespreken hoe we deze statistieken in werkelijkheid kunnen verbeteren.",
    "pagespeed.result.metric1": "Prestaties",
    "pagespeed.result.metric2": "Toegankelijkheid",
    "pagespeed.result.metric3": "SEO",
    "pagespeed.result.metric4": "Best Practices",
    "pagespeed.result.detailed": "Gedetailleerde Core Web Vitals",
    "pagespeed.result.cta": "Ontvang volledig rapport",
    "pagespeed.result.scan_another": "Nieuwe analyse",
    "pagespeed.error.demand": "De analysedienst is momenteel overbelast. Probeer het over enkele minuten opnieuw of neem contact met ons op voor een handmatige audit.",
    "pagespeed.error.unreachable": "We konden die website niet bereiken. Controleer de URL en zorg ervoor dat de site openbaar toegankelijk is.",
    "pagespeed.error.generic": "Er is een fout opgetreden bij het analyseren van de website.",
    
    "footer.desc": "Intelligente oplossingen voor de toekomst.",
    "footer.privacy": "Privacybeleid",
    "footer.terms": "Algemene voorwaarden",
    "footer.rights": "Alle rechten voorbehouden.",
    
    "about.title1": "Uw strategische partner in",
    "about.title2": "digitaal succes.",
    "about.desc": "Vanuit Lommel bouwen wij aan digitale systemen met één duidelijk doel: meetbare groei voor uw onderneming. Wij geloven in maatwerk, directe communicatie en langetermijndenken.",
    "about.val1.title": "Strategisch partnerschap",
    "about.val1.desc": "We denken proactief met u mee en bouwen systemen die uw bedrijfsvoering echt ondersteunen.",
    "about.val2.title": "Directe communicatie",
    "about.val2.desc": "Korte lijnen, heldere afspraken en een aanpak zonder onnodig jargon.",
    "about.val3.title": "Maatwerk & kwaliteit",
    "about.val3.desc": "Geen bandwerk, maar zorgvuldig ontwikkelde oplossingen die perfect passen bij uw onderneming.",

    "process.badge": "Onze aanpak",
    "process.title1": "Van strategie",
    "process.title2": "tot uitvoering",
    "process.desc": "Wij bouwen geen losse websites, maar complete digitale systemen. Een helder proces van begin tot eind.",
    "process.1.title": "Analyse & strategie",
    "process.1.desc": "We brengen uw doelen in kaart en bepalen de juiste digitale strategie voor uw bedrijf.",
    "process.2.title": "Ontwerp & uitwerking",
    "process.2.desc": "We bouwen een professionele website en zetten de nodige systemen klaar.",
    "process.3.title": "Automatisatie & koppelingen",
    "process.3.desc": "We integreren uw CRM, agenda en slimme workflows voor maximale efficiëntie.",
    "process.4.title": "Optimalisatie & opvolging",
    "process.4.desc": "We blijven de prestaties meten en sturen bij waar nodig voor blijvende groei.",

    "deliverables.badge": "Wat u krijgt",
    "deliverables.title1": "Concrete",
    "deliverables.title2": "resultaten",
    "deliverables.desc": "Een digitaal fundament dat uw bedrijf professioneler maakt en u tijd bespaart.",
    "deliverables.1": "Professionele website op maat",
    "deliverables.2": "Geoptimaliseerde leadcaptatie",
    "deliverables.3": "Naadloze CRM-integratie",
    "deliverables.4": "Slimme AI-toepassingen",
    "deliverables.5": "Geautomatiseerd boekingssysteem",
    "deliverables.6": "Tijdbesparende workflows",
    "deliverables.7": "Slimme opvolgsystemen",
    "deliverables.8": "Inzichtelijke rapportage",

    "trust.badge": "Waarom ALEXAI",
    "trust.title1": "Gebouwd voor",
    "trust.title2": "ambitieuze bedrijven",
    "trust.desc": "Digitale oplossingen voor ondernemingen die professioneler willen groeien online.",
    "trust.1.title": "Transparante samenwerking",
    "trust.1.desc": "U weet altijd precies waar u aan toe bent. Geen verborgen kosten, wel duidelijke resultaten.",
    "trust.2.title": "Focus op echte groei",
    "trust.2.desc": "We bouwen systemen die niet alleen mooi zijn, maar ook daadwerkelijk bijdragen aan uw omzet.",
    "trust.3.title": "Moderne technologie",
    "trust.3.desc": "We gebruiken de nieuwste en meest betrouwbare tools om uw bedrijf een voorsprong te geven.",
    "trust.4.title": "Volledig op uw maat",
    "trust.4.desc": "We luisteren naar uw specifieke noden en bouwen een systeem dat uw manier van werken versterkt.",

    "contact.btn1": "Vraag een gratis digitale audit aan",
    
    "about.vision.title": "Onze visie op digitaal ondernemen",
    "about.vision.desc": "Veel KMO's en dienstverleners worstelen niet met hun aanbod, maar met een verouderde digitale aanwezigheid die hun ambities afremt. Bij ALEXAI geloven we dat een website meer moet zijn dan een online visitekaartje. Het moet het hart vormen van uw bedrijfsvoering - een systeem dat vertrouwen uitstraalt, leads genereert en u tijd bespaart door slimme automatisatie. Wij bouwen die systemen, zodat u zich kunt focussen op uw vak.",

    "testimonials.badge": "Onze klanten",
    "testimonials.title1": "Digitale oplossingen",
    "testimonials.title2": "die vooruithelpen",
    "testimonials.desc": "Ontdek hoe wij KMO's en ambitieuze ondernemers helpen om hun digitale aanwezigheid te versterken.",
    "testimonials.placeholder": "Case studies en succesverhalen van onze klanten komen binnenkort online.",
    
    "roi.badge": "ROI Calculator",
    "roi.title1": "Bereken uw",
    "roi.title2": "besparing",
    "roi.desc": "Ontdek hoeveel tijd en geld onze geautomatiseerde systemen uw bedrijf kunnen besparen.",
    "roi.hours": "Manuele uren per week (bijv. administratie, e-mails)",
    "roi.rate": "Gemiddeld uurtarief (€)",
    "roi.saved.time": "Uren bespaard per maand",
    "roi.saved.money": "Geld bespaard per jaar",
    "roi.cta": "Start met automatiseren",

    "chatbot.title": "ALEXAI Assistent",
    "chatbot.status": "Online",
    "chatbot.placeholder": "Typ uw bericht...",
    "chatbot.welcome": "Hallo! Ik ben de digitale assistent van ALEXAI. Hoe kan ik u helpen vandaag?",
    "chatbot.response": "Bedankt voor uw bericht. Omdat dit een demo is, kan ik nu niet direct antwoorden. U kunt wel een gesprek boeken met ons team om te zien hoe ik voor uw bedrijf kan werken!",

    "cases.badge": "Portfolio",
    "cases.title1": "Recente",
    "cases.title2": "Case Studies",
    "cases.desc": "Bekijk enkele van onze recente projecten en ontdek hoe we de digitale werking van onze klanten hebben getransformeerd.",
    "cases.view": "Bekijk Project",

    "blog.badge": "Blog",
    "blog.title": "Inzichten over websites, automatisatie en digitale groei",
    "blog.desc": "Praktische artikels over websites, funnels, automatisatie en hoe bedrijven hun digitale werking slimmer kunnen aanpakken.",
    "blog.category.all": "Alle",
    "blog.view_all": "Bekijk alle artikels",
    "blog.cta.title": "Klaar om uw digitale basis slimmer aan te pakken?",
    "blog.cta.desc": "ALEXAI bouwt websites, funnels en geautomatiseerde systemen voor bedrijven die professioneler willen groeien online.",
    "blog.cta.btn": "Boek een strategiegesprek",
    "blog.more": "Meer artikels"
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>("en");

  // Load from local storage if available, otherwise auto-detect
  useEffect(() => {
    const saved = localStorage.getItem("language") as Language;
    if (saved && (saved === "en" || saved === "nl")) {
      setLanguage(saved);
    } else {
      // Auto-detect based on IP country
      fetch("https://get.geojs.io/v1/ip/country.json")
        .then((res) => res.json())
        .then((data) => {
          if (data.country === "NL" || data.country === "BE") {
            setLanguage("nl");
          } else {
            setLanguage("en");
          }
        })
        .catch((err) => {
          console.error("Failed to detect country:", err);
          setLanguage("en"); // Fallback
        });
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key: string): string => {
    return (translations[language] as any)[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
